<!-- SPDX-License-Identifier: EUPL-1.2 -->
<!-- SPDX-FileCopyrightText: 2026 Conduction B.V. <info@conduction.nl> -->
<template>
	<CnWizardDialog
		ref="wizard"
		:steps="wizardSteps"
		:dialogTitle="resolvedDialogTitle"
		:submitLabel="submitLabel"
		:cancelLabel="cancelLabel"
		:nextLabel="nextLabel"
		:backLabel="backLabel"
		:successText="successText"
		:validate="validateStep"
		:cancellable="cancellable"
		:initialStep="initialStepId"
		@stepChange="onStepChange"
		@submit="onSubmit"
		@close="onClose">
		<!-- This `<template v-for>` defines dynamic SLOTS, not a rendered list.
		     `@vue/compiler-sfc` DISCARDS a `:key` on a slot-defining
		     `<template>` (verified against the generated `createSlots` call)
		     and honours it only on the child, so the rule's advice is inverted
		     here — moving the key up would throw it away. -->
		<!-- eslint-disable vue/no-v-for-template-key-on-child -->
		<template v-for="step in setupSteps" #[stepSlot(step)]="scope">
			<div :key="step.id" class="cn-setup-step" :data-step-type="step.type">
				<!-- @slot step-{id} Override a step's body (for `component` steps or
				     any bespoke step). Scope: the CnWizardDialog step scope plus
				     `{ step, runAction, saveConfig }`. -->
				<slot
					v-if="hasCustomSlot(step.id)"
					:name="stepSlot(step)"
					v-bind="scope"
					:step="step"
					:runAction="() => runAction(step)"
					:saveConfig="(patch) => saveConfig(patch)" />

				<!-- info -->
				<template v-else-if="step.type === 'info'">
					<NcNoteCard type="info" :heading="stepTitle(step)">
						{{ stepBody(step) }}
					</NcNoteCard>
				</template>

				<!-- choice -->
				<template v-else-if="step.type === 'choice'">
					<NcNoteCard v-if="step.body" type="info">
						{{ stepBody(step) }}
					</NcNoteCard>
					<CnChoiceCards
						v-if="isCardChoice(step)"
						:label="stepTitle(step)"
						:options="optionsFor(step)"
						:multiple="step.multiple === true"
						:disabled="isChoiceDisabled(step)"
						:loading="isOptionsLoading(step)"
						:modelValue="cardModel(step)"
						@update:modelValue="(v) => onChoice(step, v)" />
					<NcSelect
						v-else
						:inputLabel="stepTitle(step)"
						:options="optionsFor(step)"
						:multiple="step.multiple === true"
						:disabled="isChoiceDisabled(step)"
						label="label"
						:modelValue="choiceModel[step.id]"
						@update:modelValue="(v) => onChoice(step, v)" />
					<NcNoteCard v-if="isChoiceDisabled(step)" type="warning">
						{{ dependsOnHint(step) }}
					</NcNoteCard>
				</template>

				<!-- config-fields -->
				<template v-else-if="step.type === 'config-fields'">
					<div
						v-for="field in fieldsFor(step)"
						:key="field.key"
						class="cn-setup-field">
						<NcCheckboxRadioSwitch
							v-if="field.widget === 'checkbox'"
							:modelValue="!!configModel[field.key]"
							@update:modelValue="(v) => configModel[field.key] = v">
							{{ field.label }}
						</NcCheckboxRadioSwitch>
						<NcSelect
							v-else-if="field.widget === 'select'"
							:inputLabel="field.label"
							:options="field.enum || []"
							:modelValue="configModel[field.key]"
							@update:modelValue="(v) => configModel[field.key] = v" />
						<NcTextField
							v-else
							:label="field.label"
							:type="field.widget === 'number' ? 'number' : 'text'"
							:modelValue="configModel[field.key] != null ? String(configModel[field.key]) : ''"
							@update:modelValue="(v) => configModel[field.key] = v" />
					</div>
				</template>

				<!-- run-action -->
				<template v-else-if="step.type === 'run-action'">
					<NcNoteCard v-if="step.body" type="info">
						{{ stepBody(step) }}
					</NcNoteCard>
					<!-- The action starts automatically on entering this step
					     (see `maybeAutoRunStep`), so the default state is the
					     spinner, not an idle button. -->
					<div v-if="running[step.id]" class="cn-setup-step__running">
						<NcLoadingIcon :size="20" />
						<span>{{ runningLabel }}</span>
					</div>
					<template v-else>
						<NcNoteCard
							v-if="actionResult[step.id]"
							:type="actionResult[step.id].success ? 'success' : 'error'">
							{{ actionResult[step.id].message }}
						</NcNoteCard>
						<div class="cn-setup-step__nav">
							<NcButton :variant="actionResult[step.id] ? 'secondary' : 'primary'" @click="runAction(step)">
								{{ actionResult[step.id] ? rerunLabel : runLabel }}
							</NcButton>
						</div>
					</template>
				</template>

				<!-- summary -->
				<template v-else-if="step.type === 'summary'">
					<NcNoteCard v-if="step.body" type="info">
						{{ stepBody(step) }}
					</NcNoteCard>
					<ul class="cn-setup-summary">
						<li
							v-for="item in summaryItems"
							:key="item.id"
							class="cn-setup-summary__item"
							:class="{ 'cn-setup-summary__item--done': item.done }">
							<span class="cn-setup-summary__mark">{{ item.done ? '✓' : '○' }}</span>
							<span class="cn-setup-summary__label">{{ item.title }}</span>
							<span v-if="item.value" class="cn-setup-summary__value">{{ item.value }}</span>
						</li>
					</ul>
				</template>
			</div>
		</template>
		<!-- eslint-enable vue/no-v-for-template-key-on-child -->
	</CnWizardDialog>
</template>

<script>
import { translate as t } from '@nextcloud/l10n'
import { NcButton, NcCheckboxRadioSwitch, NcLoadingIcon, NcNoteCard, NcSelect, NcTextField } from '@nextcloud/vue'
import CnChoiceCards from '../CnChoiceCards/CnChoiceCards.vue'
import CnWizardDialog from '../CnWizardDialog/CnWizardDialog.vue'
import { useSetupStatus } from '../../composables/useSetupStatus.js'
import { fieldsFromSchema } from '../../utils/schema.js'

/**
 * CnSetupWizard — abstract, manifest-driven first-time setup wizard (ADR-042).
 *
 * Wraps {@link CnWizardDialog} and renders a `manifest.setup.steps[]` array by
 * each step's `type`: `info` (note), `config-fields` (fields from a JSON Schema
 * via `fieldsFromSchema`, the same field shapes the admin pages use), `choice`
 * (`NcSelect` persisting to an app-config key), `run-action` (POST a privileged
 * server action), `summary` (recap), and `component` (a parent-provided
 * `#step-<id>` slot). It NEVER writes OpenRegister objects from the browser —
 * all persistence goes through the per-app setup contract
 * (`POST /apps/{appId}/api/setup/config` and `/api/setup/action/{actionId}`).
 *
 * Mount it gated by `CnAppRoot`'s setup phase, or open it on demand from
 * `CnAdminSettingsShell`.
 *
 * ```vue
 * <CnSetupWizard
 *   :app-id="'procest'"
 *   :steps="manifest.setup.steps"
 *   @complete="onSetupComplete"
 *   @close="showWizard = false" />
 * ```
 */
export default {
	name: 'CnSetupWizard',

	components: {
		CnWizardDialog,
		CnChoiceCards,
		NcButton,
		NcNoteCard,
		NcSelect,
		NcTextField,
		NcCheckboxRadioSwitch,
		NcLoadingIcon,
	},

	inject: {
		/**
		 * Consumer translation function, provided by CnAppRoot as
		 * `cnTranslate: this.translate` (bound to the host app's id). Step field
		 * labels come from schema property titles, authored in English as the
		 * canonical source; the visible label is resolved through this function
		 * so it follows the user's language. Defaults to identity when used
		 * standalone (no CnAppRoot ancestor).
		 */
		cnTranslate: { default: () => (key) => key },
	},

	props: {
		/** The Nextcloud app id; used to build the `/apps/{appId}/api/setup/*` URLs. */
		appId: {
			type: String,
			required: true,
		},

		/** The `manifest.setup.steps` array to render. */
		steps: {
			type: Array,
			default: () => [],
		},

		/** Dialog title. Overridden by `appName` when that prop is set. */
		dialogTitle: {
			type: String,
			default: () => t('nextcloud-vue', 'Set up this app'),
		},

		/**
		 * The app's human-readable display name. When set, the dialog title
		 * becomes "Set up {appName}" instead of the generic `dialogTitle`
		 * default — pass the same display name CnAppRoot already resolves
		 * (`appDisplayName || manifest.name || appId`).
		 */
		appName: {
			type: String,
			default: '',
		},

		/** Final-step submit button label. */
		submitLabel: {
			type: String,
			default: () => t('nextcloud-vue', 'Finish'),
		},

		/** Cancel button label. */
		cancelLabel: {
			type: String,
			default: () => t('nextcloud-vue', 'Cancel'),
		},

		/** Next button label. */
		nextLabel: {
			type: String,
			default: () => t('nextcloud-vue', 'Next'),
		},

		/** Back button label. */
		backLabel: {
			type: String,
			default: () => t('nextcloud-vue', 'Back'),
		},

		/** Run-action button label (shown before the first automatic run). */
		runLabel: {
			type: String,
			default: () => t('nextcloud-vue', 'Run'),
		},

		/** Run-action button label offered after a run already finished. */
		rerunLabel: {
			type: String,
			default: () => t('nextcloud-vue', 'Run again'),
		},

		/** Label shown next to the spinner while a run-action step is running. */
		runningLabel: {
			type: String,
			default: () => t('nextcloud-vue', 'Loading…'),
		},

		/** Result-phase success text. */
		successText: {
			type: String,
			default: () => t('nextcloud-vue', 'Setup complete.'),
		},

		/**
		 * Whether the wizard can be dismissed before finishing (Cancel
		 * button, ESC, backdrop click). Pass `false` when a REQUIRED step
		 * is unmet and the host is gating its shell behind this wizard
		 * (ADR-042) — an offered-but-non-functional Cancel would be
		 * misleading. Optional-only setup stays cancellable (default).
		 *
		 * @type {boolean}
		 */
		cancellable: {
			type: Boolean,
			default: true,
		},

		/**
		 * Ids of steps the SERVER already reports as done (e.g.
		 * `useSetupStatus(...).steps` filtered to `done === true`), passed by
		 * the host so a freshly (re)mounted wizard doesn't re-ask something
		 * already persisted in an earlier session — this component's own
		 * `localDone`/`choiceModel` only track the CURRENT session and reset
		 * blank on every mount. Drives both the initial step (`initialStepId`)
		 * and the summary page's done markers.
		 *
		 * @type {Array<string>}
		 */
		completedStepIds: {
			type: Array,
			default: () => [],
		},
	},

	emits: ['complete', 'close', 'step-change', 'action-result'],

	/**
	 * Live option lists come from the app's own setup status document, so a
	 * step that offers a choice of things the SERVER ships never has to
	 * restate them in the manifest. `useSetupStatus` caches per app id, so the
	 * host's own status fetch (CnAppRoot's setup gate) is the one that runs —
	 * this reads the same refs rather than asking again.
	 *
	 * Only called when a step actually declares `optionsSource`: a wizard with
	 * none must not start making requests it never made before.
	 *
	 * @param {object} props The component props.
	 * @return {object} `setupStatus` / `setupStatusLoading` for the option resolver.
	 */
	setup(props) {
		const wantsSource = (props.steps || []).some((s) => s && s.optionsSource)
		if (!wantsSource) {
			return {}
		}
		const { status, loading } = useSetupStatus(props.appId, { setup: { steps: props.steps } })
		return { setupStatus: status, setupStatusLoading: loading }
	},

	data() {
		return {
			choiceModel: {},
			choiceValues: {},
			configModel: {},
			running: {},
			actionResult: {},
			localDone: {},
			userTouched: {},
		}
	},

	computed: {
		/**
		 * The title actually rendered by CnWizardDialog: "Set up {appName}"
		 * when a display name was passed, otherwise whatever `dialogTitle`
		 * resolves to (a caller override, or its own generic default).
		 *
		 * @return {string}
		 */
		resolvedDialogTitle() {
			return this.appName
				? t('nextcloud-vue', 'Set up {app}', { app: this.appName })
				: this.dialogTitle
		},

		setupSteps() {
			return (this.steps || []).filter((s) => s && s.id && s.type)
		},

		wizardSteps() {
			return this.setupSteps.map((s) => ({
				id: s.id,
				label: this.stepTitle(s),
				optional: s.required !== true,
			}))
		},

		/**
		 * Recap rows for the `summary` step — one per non-summary step, with
		 * the value the user selected (choice label / config field values) and
		 * a done marker (info steps always done; choice steps done when picked;
		 * run-action steps done when the action succeeded).
		 *
		 * @return {Array<{ id: string, title: string, value: string, done: boolean }>}
		 */
		summaryItems() {
			return this.setupSteps
				.filter((s) => s.type !== 'summary')
				.map((step) => {
					let value = ''
					if (step.type === 'choice') {
						const raw = this.choiceModel[step.id]
						if (Array.isArray(raw)) {
							value = raw.map((o) => this.choiceLabel(step, o)).join(', ')
						} else if (raw !== null && raw !== undefined && raw !== '') {
							value = this.choiceLabel(step, raw)
						}
					} else if (step.type === 'config-fields') {
						value = this.fieldsFor(step)
							.map((f) => `${f.label}: ${this.configModel[f.key] !== null && this.configModel[f.key] !== undefined ? this.configModel[f.key] : ''}`)
							.join(', ')
					}
					let done
					if (step.type === 'info') {
						done = true
					} else if (step.type === 'choice') {
						done = this.hasChoice(step) || this.isServerDone(step.id)
					} else {
						done = this.isStepDone(step.id)
					}
					return { id: step.id, title: this.stepTitle(step), value, done }
				})
		},

		/**
		 * Id of the step the wizard should open on when RESUMING: the first
		 * non-`info`/`summary` step not already done, per the CURRENT session's
		 * local state OR the server-reported `completedStepIds` (a prior
		 * session). Falls back to `''` (CnWizardDialog's own first-step default)
		 * on a fresh setup and when every actionable step is already done.
		 *
		 * @return {string}
		 */
		initialStepId() {
			// Fresh setup — nothing done anywhere yet, so there is nothing to
			// resume past. Start at step one so a leading `info`/welcome step is
			// actually seen; resuming is only for returning sessions.
			if (this.completedStepIds.length === 0) {
				return ''
			}
			const actionable = this.setupSteps.filter((s) => s.type !== 'info' && s.type !== 'summary')
			const firstUnmet = actionable.find((s) => {
				if (s.type === 'choice') {
					return !(this.hasChoice(s) || this.isServerDone(s.id))
				}
				return !this.isStepDone(s.id)
			})
			return firstUnmet ? firstUnmet.id : ''
		},
	},

	mounted() {
		// Covers a RESUMED wizard opening straight onto its run-action step
		// (`initialStepId`) — `onStepChange` only fires on later navigation,
		// so a step already active at mount time needs its own trigger.
		this.maybeAutoRunStep(this.initialStepId || (this.setupSteps[0] && this.setupSteps[0].id))
	},

	methods: {
		/**
		 * Start a `run-action` step's action automatically the moment it
		 * becomes current, instead of waiting for a manual "Run" click —
		 * the step has nothing else for the user to decide by then (the
		 * choice it acts on was already made on an earlier step), so
		 * requiring a click before showing progress just adds a stall.
		 * No-ops for any other step type, or one already run/running.
		 *
		 * @param {string} stepId The step id becoming current.
		 * @return {void}
		 */
		maybeAutoRunStep(stepId) {
			const step = this.setupSteps.find((s) => s.id === stepId)
			if (step && step.type === 'run-action' && !this.isStepDone(step.id) && !this.running[step.id]) {
				this.runAction(step)
			}
		},

		/**
		 * The visible title for a step, resolved through the host app's
		 * translation function.
		 *
		 * `manifest.setup.steps[].title` is authored in English as the
		 * canonical source, exactly like the schema property titles this
		 * component already routes through `cnTranslate` for field labels
		 * (see `fieldsFromSchema` below). The step titles were the one
		 * remaining place rendering the manifest string verbatim, so an
		 * nl-locale user saw a wizard whose field labels were translated and
		 * whose step headings were not.
		 *
		 * Falls back to the step id when a step declares no title, which is
		 * the same fallback the raw bindings used.
		 *
		 * @param {object} step A `manifest.setup.steps[]` entry.
		 * @return {string} The translated title, or the step id.
		 */
		stepTitle(step) {
			const raw = (step && step.title) || (step && step.id) || ''
			return raw === '' ? '' : this.cnTranslate(raw)
		},

		/**
		 * The visible body for a step, resolved through the host app's
		 * translation function.
		 *
		 * 🔴 THIS USED TO RENDER THE MANIFEST STRING VERBATIM, IN FOUR PLACES.
		 * `stepTitle()` was routed through `cnTranslate` and the body was not,
		 * so a wizard on a Dutch instance showed a translated heading over an
		 * English paragraph, with translated Cancel/Next buttons underneath.
		 * That reads as a bug rather than as a missing string, and the app could
		 * not fix it: decidiq shipped correct Dutch for its setup copy in
		 * `l10n/nl.json`, and the component never asked for it.
		 *
		 * @param {object} step The step declaration.
		 * @return {string} The translated body, or ''.
		 */
		stepBody(step) {
			const raw = (step && step.body) || ''
			return raw === '' ? '' : this.cnTranslate(raw)
		},

		stepSlot(step) {
			return 'step-' + step.id
		},

		hasCustomSlot(id) {
			return !!this.$slots['step-' + id] || !!this.$slots['step-' + id]
		},

		fieldsFor(step) {
			if (step.schema && typeof step.schema === 'object') {
				return fieldsFromSchema(step.schema, { translate: this.cnTranslate })
			}
			// Fallback: a plain text field per declared config key.
			return (step.configKeys || []).map((key) => ({ key, label: key, widget: 'text' }))
		},

		/**
		 * Options for a `choice` step. When the step declares `dependsOn`
		 * (a parent step's configKey) + `optionsByParent`, the option list
		 * is the entry keyed by the parent's currently-selected value — this
		 * is the country → organisation-type cascade. Otherwise the static
		 * `options` array.
		 *
		 * @param {object} step The choice step definition.
		 * @return {Array} The option list to render.
		 */
		optionsFor(step) {
			let options = step.options || []
			if (step.optionsSource) {
				options = this.sourcedOptions(step)
			} else if (step.dependsOn && step.optionsByParent) {
				const parentValue = this.choiceValues[step.dependsOn]
				options = (parentValue === null || parentValue === undefined || parentValue === '')
					? []
					: (step.optionsByParent[parentValue] || [])
			}
			// The LABEL is translated, the VALUE never is. `scalarChoice()` reads
			// `.value` and that is what reaches `POST /api/setup/config`, so a
			// translated label cannot change what gets stored — while an
			// untranslated one leaves the choice itself in English on a
			// localised instance, which is the half-translated screen this fix
			// exists to remove.
			return options.map((option) => {
				if (!option || typeof option !== 'object' || !option.label) {
					return option
				}
				const translated = { ...option, label: this.cnTranslate(option.label) }
				if (option.description) {
					translated.description = this.cnTranslate(option.description)
				}
				return translated
			})
		},

		/**
		 * The option list a `choice` step declares `optionsSource` for, read
		 * from the setup status document the app already serves.
		 *
		 * `optionsSource` names a key in that document (dots walk into nested
		 * objects), and the entries are normalised to the option shape: `id` is
		 * accepted for `value`, `name` for `label`, and a bare `objectCount`
		 * becomes the one stat a card shows. A server that already sends
		 * `stats` keeps them.
		 *
		 * @param {object} step The choice step declaring `optionsSource`.
		 * @return {Array<object>} The options, or [] while the status is loading.
		 */
		sourcedOptions(step) {
			let node = this.setupStatus
			for (const key of String(step.optionsSource).split('.')) {
				if (node === null || node === undefined || typeof node !== 'object') {
					return []
				}
				node = node[key]
			}
			if (!Array.isArray(node)) {
				return []
			}
			return node
				.filter((entry) => entry && typeof entry === 'object')
				.map((entry) => {
					const value = entry.value !== undefined ? entry.value : entry.id
					const option = {
						...entry,
						value,
						label: entry.label || entry.name || String(value),
					}
					if (!option.stats && typeof entry.objectCount === 'number' && entry.objectCount > 0) {
						option.stats = [{ label: t('nextcloud-vue', 'Objects'), value: entry.objectCount }]
					}
					return option
				})
				.filter((option) => option.value !== undefined)
		},

		/**
		 * Whether a choice step renders as a card grid rather than a dropdown.
		 *
		 * @param {object} step The choice step.
		 * @return {boolean} True for `display: "cards"`.
		 */
		isCardChoice(step) {
			return step.display === 'cards'
		},

		/**
		 * Whether this step is still waiting for its live option list.
		 *
		 * @param {object} step The choice step.
		 * @return {boolean} True while the status document is in flight.
		 */
		isOptionsLoading(step) {
			return !!step.optionsSource && this.setupStatusLoading === true
		},

		/**
		 * The card grid's model: plain values, not the option objects NcSelect
		 * binds. `scalarChoice` already reduces either shape to values, so both
		 * renderers can share one `choiceModel` entry.
		 *
		 * @param {object} step The choice step.
		 * @return {unknown} The selected value, or [] / null when nothing is picked.
		 */
		cardModel(step) {
			const value = this.scalarChoice(step)
			if (step.multiple === true) {
				return Array.isArray(value) ? value : []
			}
			return value === undefined ? null : value
		},

		isChoiceDisabled(step) {
			if (!step.dependsOn) {
				return false
			}
			const parentValue = this.choiceValues[step.dependsOn]
			return parentValue === null || parentValue === undefined || parentValue === ''
		},

		dependsOnHint(step) {
			const parent = this.setupSteps.find((s) => s.configKey === step.dependsOn)
			const label = parent ? (parent.title || parent.id) : step.dependsOn
			return t('nextcloud-vue', 'Select "{step}" first.', { step: label })
		},

		/**
		 * The visible label for one selected choice entry.
		 *
		 * A card grid stores plain VALUES where NcSelect stores option objects,
		 * so the recap looked one up and printed the other: the summary read
		 * "municipality" instead of "Municipality". Resolves a bare value back
		 * to its option, and falls back to the value when the option list is
		 * not (yet) available.
		 *
		 * @param {object} step  The choice step.
		 * @param {unknown}      entry One selected entry: an option object or a value.
		 * @return {string} The label to show.
		 */
		choiceLabel(step, entry) {
			if (entry && typeof entry === 'object' && entry.label) {
				return entry.label
			}
			const match = this.optionsFor(step).find((o) => o && String(o.value) === String(entry))
			return (match && match.label) ? match.label : String(entry)
		},

		hasChoice(step) {
			const v = this.choiceModel[step.id]
			if (step.multiple === true) {
				return Array.isArray(v) && v.length > 0
			}
			return v !== null && v !== undefined && v !== ''
		},

		scalarChoice(step) {
			const raw = this.choiceModel[step.id]
			if (step.multiple === true) {
				return (raw || []).map((o) => (o && o.value !== undefined ? o.value : o))
			}
			return raw && raw.value !== undefined ? raw.value : raw
		},

		onChoice(step, value) {
			this.userTouched[step.id] = true
			this.choiceModel[step.id] = value
			if (step.configKey) {
				this.choiceValues[step.configKey] = this.scalarChoice(step)
			}
			// Reset any dependent child choices when the parent changes.
			for (const child of this.setupSteps) {
				if (child.dependsOn && child.dependsOn === step.configKey) {
					this.choiceModel[child.id] = child.multiple === true ? [] : null
					this.userTouched[child.id] = false
					if (child.configKey) {
						this.choiceValues[child.configKey] = ''
					}
				}
			}
			// Re-apply auto-suggestions for steps that derive a default from this one.
			for (const dep of this.setupSteps) {
				if (dep.suggestFrom && dep.suggestFrom === step.configKey) {
					this.applySuggestion(dep)
				}
			}
		},

		/**
		 * Auto-fill a `choice` step's suggested default from an earlier choice
		 * (its `suggestFrom` configKey → `suggestMap[parentValue]`). No-op once
		 * the user has manually picked here, so a suggestion never clobbers an
		 * explicit choice.
		 *
		 * @param {object} step The dependent choice step.
		 * @return {void}
		 */
		applySuggestion(step) {
			if (this.userTouched[step.id] === true) {
				return
			}
			const parentValue = this.choiceValues[step.suggestFrom]
			if (parentValue === null || parentValue === undefined || parentValue === '') {
				return
			}
			const wanted = (step.suggestMap || {})[parentValue]
			if (wanted === null || wanted === undefined) {
				return
			}
			const opt = this.optionsFor(step).find((o) => o.value === wanted) || { value: wanted, label: String(wanted) }
			this.choiceModel[step.id] = opt
			if (step.configKey) {
				this.choiceValues[step.configKey] = wanted
			}
		},

		/**
		 * CnWizardDialog `validate` hook — intercepts Next/Submit to persist
		 * the active step before advancing. `choice`/`config-fields` steps
		 * POST their value via the setup contract; required `choice` steps
		 * block advance until a value is picked; required `run-action` steps
		 * block until the action has succeeded. Returns `true` to advance or
		 * an error string to surface above the step and block navigation.
		 *
		 * @param {string} stepId The active step id.
		 * @return {Promise<boolean|string>} True to advance, or an error message.
		 */
		async validateStep(stepId) {
			const step = this.setupSteps.find((s) => s.id === stepId)
			if (!step) {
				return true
			}
			if (step.type === 'choice') {
				if (!this.hasChoice(step)) {
					// Already persisted server-side and the user only back-navigated
					// onto it — `choiceModel` is blank because it's session-local, not
					// because nothing is set. Don't force a re-pick, and don't re-POST.
					if (this.isServerDone(step.id)) {
						return true
					}
					return step.required === true
						? t('nextcloud-vue', 'Please make a selection to continue.')
						: true
				}
				try {
					await this.saveConfig({ [step.configKey]: this.scalarChoice(step) })
					this.localDone[step.id] = true
					return true
				} catch (err) {
					return this.errorMessage(err)
				}
			}
			if (step.type === 'config-fields') {
				try {
					const patch = {}
					for (const field of this.fieldsFor(step)) {
						patch[field.key] = this.configModel[field.key]
					}
					await this.saveConfig(patch)
					this.localDone[step.id] = true
					return true
				} catch (err) {
					return this.errorMessage(err)
				}
			}
			if (step.type === 'run-action' && step.required === true && !this.isStepDone(step.id)) {
				return t('nextcloud-vue', 'Please run this step before continuing.')
			}
			return true
		},

		/**
		 * Persist one or more app-config values via the setup contract.
		 *
		 * @param {object} patch `{ <configKey>: value }` to write.
		 * @return {Promise<void>}
		 */
		async saveConfig(patch) {
			const [{ default: axios }, { generateUrl }] = await Promise.all([
				import('@nextcloud/axios'),
				import('@nextcloud/router'),
			])
			await axios.post(generateUrl(`/apps/${this.appId}/api/setup/config`), patch)
		},

		async runAction(step) {
			this.running[step.id] = true
			this.actionResult[step.id] = null
			try {
				const [{ default: axios }, { generateUrl }] = await Promise.all([
					import('@nextcloud/axios'),
					import('@nextcloud/router'),
				])
				const { data } = await axios.post(generateUrl(`/apps/${this.appId}/api/setup/action/${step.action}`))
				const result = {
					success: data && data.success !== false,
					message: (data && data.message) || t('nextcloud-vue', 'Done.'),
				}
				this.actionResult[step.id] = result
				if (result.success) {
					this.localDone[step.id] = true
				}
				/**
				 * @event action-result Emitted when a `run-action` step finishes.
				 * @type {{ stepId: string, action: string, success: boolean, message: string }}
				 */
				this.$emit('action-result', { stepId: step.id, action: step.action, ...result })
			} catch (err) {
				this.actionResult[step.id] = { success: false, message: this.errorMessage(err) }
				this.$emit('action-result', { stepId: step.id, action: step.action, success: false, message: this.errorMessage(err) })
			} finally {
				this.running[step.id] = false
			}
		},

		isStepDone(id) {
			return this.isServerDone(id) || this.localDone[id] === true || (this.actionResult[id] && this.actionResult[id].success)
		},

		/**
		 * Whether the server already reported this step done in a prior
		 * session, via the `completedStepIds` prop.
		 *
		 * @param {string} id Step id.
		 * @return {boolean}
		 */
		isServerDone(id) {
			return this.completedStepIds.includes(id)
		},

		onSubmit() {
			if (this.$refs.wizard && this.$refs.wizard.setResult) {
				this.$refs.wizard.setResult({ success: true, message: this.successText })
			}
			/**
			 * @event complete Emitted when the last step is submitted (setup finished).
			 */
			this.$emit('complete')
		},

		onStepChange(payload) {
			// Pre-fill a suggested default when entering a step that derives one.
			const step = this.setupSteps.find((s) => s.id === payload.stepId)
			if (step && step.suggestFrom) {
				this.applySuggestion(step)
			}
			this.maybeAutoRunStep(payload.stepId)
			/**
			 * @event step-change Emitted when the active step changes.
			 * @type {{ stepId: string, stepIndex: number, direction: string }}
			 */
			this.$emit('step-change', payload)
		},

		onClose() {
			/**
			 * @event close Emitted when the dialog should close.
			 */
			this.$emit('close')
		},

		errorMessage(err) {
			const data = err && err.response && err.response.data
			if (data && (data.message || data.error)) {
				return data.message || data.error
			}
			return (err && err.message) || t('nextcloud-vue', 'Something went wrong.')
		},
	},
}
</script>

<style scoped>
.cn-setup-step {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.cn-setup-step__nav {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	margin-top: 8px;
}

.cn-setup-step__running {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 24px 0;
	color: var(--color-text-maxcontrast);
}

.cn-setup-field {
	display: flex;
	flex-direction: column;
}

.cn-setup-summary {
	list-style: none;
	padding: 0;
	margin: 0;
}

.cn-setup-summary__item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 0;
	color: var(--color-text-maxcontrast);
}

.cn-setup-summary__item--done {
	color: var(--color-main-text);
}

.cn-setup-summary__mark {
	font-weight: bold;
	color: var(--color-success);
}

.cn-setup-summary__label {
	flex: 0 0 auto;
}

.cn-setup-summary__value {
	margin-inline-start: auto;
	font-weight: 600;
	color: var(--color-main-text);
}
</style>
